import { useState } from "react";
import { Navigate, NavLink, Outlet, Link } from "react-router-dom";
import { BellRing, Bot, CalendarCheck, ExternalLink, FileText, Inbox, LayoutDashboard, Loader2, LogOut, Luggage, Mail, Star, Menu, MessageSquareQuote, Package, Settings, Users } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { FollowUpBell } from "@/components/admin/leads/FollowUpBell";
import { Seo } from "@/components/site/Seo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/admin/enquiries", label: "Leads", icon: Inbox, id: "enquiries" },
  { to: "/admin/auto-messages", label: "Enquiry Auto Messages", icon: Mail, id: "auto-messages" },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, id: "bookings" },
  { to: "/admin/reminders", label: "Payment Reminders", icon: BellRing, id: "reminders" },
  { to: "/admin/briefings", label: "Trip Briefings", icon: Luggage, id: "briefings" },
  { to: "/admin/feedback", label: "Feedback Requests", icon: Star, id: "feedback" },
  { to: "/admin/reviews", label: "Guest Reviews", icon: MessageSquareQuote, id: "reviews" },
  { to: "/admin/automation", label: "WhatsApp Automation", icon: Bot, id: "automation" },
  { to: "/admin/customers", label: "Customers", icon: Users, id: "customers" },
  { to: "/admin/packages", label: "Packages & Prices", icon: Package, id: "packages" },
  { to: "/admin/quotes", label: "Quotations", icon: FileText, id: "quotes" },
  { to: "/admin/settings", label: "Company Settings", icon: Settings, id: "settings" },
];

const NavItems = ({ onClick, prefix }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map(({ to, label, icon: Icon, id, end }) => (
      <NavLink key={id} to={to} end={end} onClick={onClick} data-testid={`${prefix}-${id}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-ocean text-white" : "text-slate-600 hover:bg-sky-50 hover:text-ocean"}`}>
        <Icon className="h-4 w-4" /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-ocean" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <Seo title="Admin | Ocean Wings Travel" description="Ocean Wings Travel admin." path="/admin" noindex />
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-5 sticky top-0 h-screen">
        <Logo to="/admin" compact />
        <div className="mt-10 flex-1"><NavItems prefix="admin-nav" /></div>
        <Link to="/" target="_blank" className="flex items-center gap-2 text-sm text-slate-500 hover:text-ocean px-3 py-2" data-testid="admin-view-site-link"><ExternalLink className="h-4 w-4" /> View website</Link>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 sm:px-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden h-10 w-10 grid place-items-center rounded-lg border border-slate-200" data-testid="admin-mobile-menu-button" aria-label="Menu"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-white">
              <SheetTitle className="sr-only">Admin menu</SheetTitle>
              <div className="mt-4 mb-8"><Logo to="/admin" compact /></div>
              <NavItems prefix="admin-mobile-nav" onClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <p className="hidden lg:block text-sm text-slate-500">Ocean Wings Travel · Admin</p>
          <div className="flex items-center gap-3">
            <FollowUpBell />
            <span className="hidden sm:block text-sm text-slate-600" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-rose-300 hover:text-rose-600 transition-colors" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-8 max-w-[1400px]"><Outlet /></main>
      </div>
    </div>
  );
}
